<docs>
A dropdown select, single or multi and optionally searchable, built on Reka Select: avatars, coloured
dots, two-line option rows, a tri-state "All …" master checkbox, lazy loading as you scroll, and
server-side search through a single `fetcher` function. For a menu of actions (not a held value) use
ZoneButtonDropdown.

## v-model

Single: the selected id (string | number) or `null`. `multiple`: an array of selected ids. `change`
fires with the same payload.

## Options

`{ id, name, description?, avatar?, dot?, icon?, disabled? }`: objects with `id` and `name`, not bare
strings. `icon` is a Zone icon name leading the row (primary-700 when selected, gray-600 otherwise). `dot` is a Tailwind colour name (rendered as `bg-{dot}-500`); `description` gives a two-line
row.

## Modes

Local (default): the component owns `options`, filters them on `search` and renders them a page at a
time, growing the window as you scroll. Nothing to configure; a 10k-row array stays cheap because
only `pageSize` rows are ever in the DOM.

Server: pass `fetcher` and the component stops touching `options` entirely. One function serves
search and pagination, because they are the same query: `fetcher(state)` with
`{ page, pageSize, search, selectedOnly, selected }` returns `{ options, hasMore?, totalCount? }` or a
bare `SelectOption[]`, sync or async; rejections are emitted as `fetch-error`. It is called when the
dropdown opens (page 1), when `search` changes (debounced, back to page 1, replacing the list) and
when the bottom sentinel scrolls into view (page + 1, appending). Stale responses are dropped, so a
slow page-1 answer never overwrites a newer search. `state.selectedOnly` is always false for these
list queries; it is only true for the separate label-hydration query. Omit `hasMore` and it is
inferred as `options.length >= pageSize`.

## Notes

- The dropdown teleports to `#zone-teleport` (carries `.w-tw` and z-index): query `document.body` in
  tests, not the wrapper. Opens on `pointerdown`, not `click` (on `pointerup` for touch).
- The multi-select count pill's × is a `<span>`, not a `<button>`: it lives inside Reka's trigger
  `<button>`, and a button inside a button is invalid HTML. The keyboard route is Backspace/Delete
  on the focused trigger, which clears the selection in both modes. That is also why it is not a
  ZonePill, whose dismiss is a real button.
- `clearable` (off by default) is the clear control and nothing else: the × in the count pill of a
  `multiple` select, an × before the chevron of a single one (it emits `null`), and Backspace/Delete
  on the trigger. The "All {plural}" master row belongs to every `multiple` select either way.
- Multi always shows the count pill + resource name once anything is selected, per Figma; it never
  falls back to the single item's own label.
- "All {plural}" is a true master checkbox over the rows on offer: empty when none are picked, mixed
  (`aria-checked="mixed"`, a dash) when some are, checked when all are. Clicking it selects all from
  empty or mixed, and only clears when already fully checked. Disabled rows are excluded from the
  count. It only speaks for the rows on offer, so with a search active it adds the matching rows and
  un-checking removes just those, leaving anything filtered out (and any selected-but-disabled row)
  as it was. The count pill's × and Backspace/Delete on the trigger are the clear-everything routes.
- Reopen regroups (`multiple` only): whatever was selected when the dropdown opened is listed first,
  each group keeping its original order. The grouping is keyed on a snapshot taken at open, not the
  live selection, so ticking a row does not make it jump out from under the pointer. This replaced
  the "Show only selected" toggle. A single select keeps `options`' order: its value is already in
  the trigger, and an ordinal list (months, years) must read in order.
- In server mode "All {plural}" can only speak for the options already loaded, and the
  selected-first grouping can only group those same rows; a true select-all over the whole result
  set has to be handled by the caller.
- The label is associated with the trigger via `aria-labelledby`, the caption via
  `aria-describedby`; `invalid` sets `aria-invalid` on the trigger button. With `#label` the id sits
  on the wrapper around your slot content, so keep non-label text (links, badges) out of it where
  possible.

Figma: [Usage](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=4342-18591),
[Blueprint](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=4342-18994)
</docs>

<template>
  <div :class="wrapperClasses">
    <div
      v-if="hasLabel || tooltip"
      class="flex h-[18px] items-center gap-1"
      data-testid="label-row"
    >
      <!--
        Figma's inline type leads with the info icon and suffixes the label with a colon; the
        contained type puts the icon after the label. Same two elements, swapped order.
      -->
      <ZoneIcon
        v-if="tooltip && type === 'inline'"
        icon="info"
        :tooltip="tooltip"
        :class="tooltipIconClasses"
      />

      <!--
        #label REPLACES the built-in label markup rather than rendering inside it, so callers can
        compose their own row without nesting a <label> inside a <label>. The plain wrapper div
        carries `labelId` so the trigger's aria-labelledby still resolves to the visible label.
      -->
      <div
        v-if="$slots.label"
        :id="labelId"
      >
        <slot
          name="label"
          :label="label"
          :label-id="labelId"
          :required="required"
          :invalid="invalid"
        />
      </div>
      <label
        v-else-if="label"
        :id="labelId"
        :class="labelClasses"
      >
        {{ labelText }}<span
          v-if="required"
          class="ml-0.5"
          aria-hidden="true"
          data-testid="required-asterisk"
        >*</span>
      </label>

      <ZoneIcon
        v-if="tooltip && type !== 'inline'"
        icon="info"
        :tooltip="tooltip"
        :class="tooltipIconClasses"
      />
    </div>

    <SelectRoot
      v-model:open="isOpen"
      :model-value="modelValue"
      :multiple="multiple"
      :disabled="disabled"
      @update:model-value="onModelValueUpdate"
    >
      <div class="relative font-regular">
        <SelectTrigger
          :disabled="triggerDisabled"
          :class="triggerClasses"
          aria-haspopup="listbox"
          :aria-labelledby="hasLabel ? labelId : undefined"
          :aria-describedby="hasHelper ? helperId : undefined"
          :aria-invalid="invalid || undefined"
          @keydown="onTriggerKeydown"
        >
          <!-- slot-leading: the multi count pill, or a single selection's avatar / dot -->
          <span
            v-if="hasLeading"
            class="flex h-full shrink-0 items-center justify-center px-1.5"
          >
            <span
              v-if="multiple"
              data-testid="count-pill"
              class="pointer-events-none flex shrink-0 items-center rounded-full bg-primary-100"
            >
              <span
                data-testid="count-value"
                class="px-2 text-xs leading-4 text-primary-800"
              >{{ countLabel }}</span>
              <!--
                A <span>, not a <button>: this sits inside Reka's trigger <button> and nesting
                buttons is invalid HTML. `pointer-events-auto` re-enables hits on just this box,
                and stopping the pointerdown keeps the dropdown from opening; the pointerup too,
                which is the one Reka's trigger opens on for touch. Keyboard users clear with
                Backspace/Delete on the trigger — see onTriggerKeydown.
              -->
              <span
                v-if="showClear"
                data-zone-select-clear
                aria-hidden="true"
                class="pointer-events-auto mr-1 flex size-4 cursor-pointer items-center justify-center rounded-full text-primary-700 hover:bg-primary-200"
                @pointerdown.stop.prevent="onClearAll"
                @pointerup.stop.prevent
              >
                <ZoneIcon
                  icon="close"
                  size="xs"
                />
              </span>
            </span>

            <ZoneAvatar
              v-else-if="singleSelectedItem && singleSelectedItem.avatar"
              class="pointer-events-none"
              :src="singleSelectedItem.avatar"
              :name="singleSelectedItem.name"
              size="sm"
            />
            <span
              v-else-if="singleSelectedItem && singleSelectedItem.dot"
              class="pointer-events-none size-2 shrink-0 rounded-full"
              :class="`bg-${singleSelectedItem.dot}-500`"
            />
          </span>

          <!-- selection: the value or the placeholder, always one truncating line -->
          <span class="pointer-events-none flex h-full min-w-px flex-1 items-center overflow-hidden px-1.5">
            <span
              data-testid="selection"
              :class="selectionClasses"
            >{{ selectionText }}</span>
          </span>

          <!-- slot-trailing: a single select's × once a value is picked, then the chevron; both dropped in the read-only state -->
          <span
            v-if="!readonly"
            class="pointer-events-none flex h-full shrink-0 items-center justify-center px-1.5 text-gray-600"
          >
            <!-- A <span> inside the trigger button, like the count pill's × (see the docs). -->
            <span
              v-if="showClear && !multiple"
              data-zone-select-clear
              aria-hidden="true"
              class="pointer-events-auto mr-1 flex size-4 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              @pointerdown.stop.prevent="onClearAll"
              @pointerup.stop.prevent
            >
              <ZoneIcon
                icon="close"
                size="xs"
              />
            </span>
            <ZoneIcon :icon="isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" />
          </span>
        </SelectTrigger>

        <!--
          Target `#zone-teleport` (defined in the app layouts, see application.scss) rather than
          the reka-ui default of `document.body`. It already carries `.w-tw` — required for
          Tailwind utilities to apply at all, since `important: '.w-tw'` in tailwind.config.js
          makes `.w-tw` a required ancestor, and SelectPortal's target is otherwise a plain,
          unscoped body descendant — plus the correct fixed positioning and high z-index (8030)
          that every other piece of floating/teleported Zone UI in this app already relies on.
        -->
        <SelectPortal to="#zone-teleport">
          <!-- As wide as its trigger (Reka's CSS var); Figma's floating menu floors it at 100px. -->
          <SelectContent
            position="popper"
            :side-offset="4"
            :style="{ width: 'var(--reka-select-trigger-width)' }"
            class="z-50 min-w-[100px] overflow-hidden rounded-md border border-gray-300 bg-white text-base shadow-md focus:outline-none"
            @click.stop
          >
            <!--
              reka-ui keeps SelectContent's slot mounted at all times (moved into a detached,
              off-DOM fragment while closed) so SelectValue can show a label without the dropdown
              ever having opened. ZoneSelect doesn't use SelectValue — the trigger label is
              computed from the `options`/`modelValue` props directly — so that's pure overhead
              here. With several ZoneSelects on one page (each mounted for the lifetime of its
              parent, not just its own open state — e.g. every field across every tab of the
              Member dialog, since inactive tabs stay mounted too) and some backed by long option
              lists, that overhead compounds into hundreds/thousands of permanently-live
              SelectItem instances. Gating on `isOpen` here restores "nothing renders until
              opened", matching the original custom implementation's behavior.
            -->
            <template v-if="isOpen">
              <!--
                The header holds the search field and nothing else. It is gated on `searchable`
                alone, not `searchable || multiple`: the "Show only selected" toggle that used to
                sit beside it is gone (selected rows group to the top of the list instead — see
                `orderedOptions`), so on a multi-select without search this box would otherwise
                render as an empty 16px-padded strip.
              -->
              <div
                v-if="searchable"
                class="flex w-full flex-col gap-3 border-b border-gray-200 bg-white p-4"
              >
                <ZoneSearchInput
                  ref="searchInput"
                  size="small"
                  variant="square"
                  :model-value="searchQuery"
                  :placeholder="searchPlaceholder"
                  @update:model-value="onSearchInput"
                  @keydown="onSearchKeydown"
                />
              </div>

              <SelectViewport
                ref="viewport"
                class="max-h-[300px] overflow-x-hidden overflow-y-auto"
                @scroll="onViewportScroll"
              >
                <!--
                  The master checkbox for the rows on offer. role=checkbox + aria-checked carry
                  the tri-state to assistive tech ('mixed' is the ARIA spelling of partial); it
                  stays a <button> because a real <input> cannot live inside Reka's listbox.
                -->
                <button
                  v-if="multiple"
                  type="button"
                  role="checkbox"
                  :aria-checked="selectAllState"
                  data-testid="select-all"
                  class="flex w-full items-center gap-2 border-b border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
                  @click.stop="onToggleAll"
                >
                  <span :class="checkboxClasses(selectAllState !== 'false')">
                    <ZoneIcon
                      v-if="selectAllGlyph"
                      :icon="selectAllGlyph"
                      size="xs"
                    />
                  </span>
                  <span class="min-w-0 flex-1 truncate text-sm leading-5 text-gray-900">
                    {{ t('all', { plural: resource.plural }) }}
                  </span>
                </button>

                <SelectItem
                  v-for="(item, index) in visibleOptions"
                  :key="itemKey(item, index)"
                  :value="item.id"
                  :disabled="isItemDisabled(item)"
                  class="flex w-full cursor-pointer select-none items-center gap-2 border-b border-gray-200 px-4 py-1.5 text-left focus:outline-none"
                  :class="[optionRowClasses(item), { 'cursor-not-allowed opacity-60': isItemDisabled(item) }]"
                >
                  <span
                    v-if="multiple"
                    data-testid="option-checkbox"
                    :class="checkboxClasses(isSelected(item.id))"
                  >
                    <ZoneIcon
                      v-if="isSelected(item.id)"
                      icon="check"
                      size="xs"
                    />
                  </span>

                  <!--
                    Same colour rule as ZoneFloatingMenu's rows: primary-700 on the selected row, gray-600
                    otherwise. `leading-5` pins the 18px glyph to the row text's 20px line box: on its own
                    `text-lg` brings a 26px line-height along and the row grows 6px taller than its siblings.
                  -->
                  <ZoneIcon
                    v-if="item.icon"
                    :icon="item.icon"
                    size="lg"
                    data-testid="option-icon"
                    :class="isSelected(item.id) ? 'shrink-0 leading-5 text-primary-700' : 'shrink-0 leading-5 text-gray-600'"
                  />
                  <ZoneAvatar
                    v-else-if="item.avatar"
                    :src="item.avatar"
                    :name="item.name"
                    size="sm"
                  />
                  <span
                    v-else-if="item.dot"
                    class="size-2 shrink-0 rounded-full"
                    :class="`bg-${item.dot}-500`"
                  />

                  <SelectItemText as-child>
                    <div
                      v-if="item.description"
                      class="flex min-w-0 flex-1 flex-col pr-2 text-left"
                    >
                      <span
                        data-testid="option-name"
                        class="min-w-0 truncate text-xs font-semibold leading-4 text-gray-900"
                      >{{ item.name }}</span>
                      <span class="min-w-0 truncate text-xs leading-4 text-gray-500">{{ item.description }}</span>
                    </div>
                    <div
                      v-else
                      data-testid="option-name"
                      class="min-w-0 flex-1 truncate pr-2 text-left text-sm leading-5 text-gray-900"
                    >{{ item.name }}</div>
                  </SelectItemText>
                </SelectItem>

                <div
                  v-if="isFetching"
                  data-testid="loading"
                  class="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500"
                >
                  <ZoneIcon
                    icon="progress_activity"
                    class="animate-spin"
                  />
                  {{ t('loading') }}
                </div>

                <div
                  v-else-if="!visibleOptions.length"
                  data-testid="empty"
                  class="px-4 py-6 text-center text-sm text-gray-500"
                >
                  {{ emptyText }}
                </div>

                <!--
                  The infinite-scroll trip wire. It only exists while there is another page to
                  load, so it unmounts (and stops observing) during a fetch — which is what keeps
                  a single scroll from firing several page loads.
                -->
                <div
                  v-if="canLoadMore"
                  ref="sentinel"
                  data-testid="load-more-sentinel"
                  class="h-px w-full"
                />
              </SelectViewport>
            </template>
          </SelectContent>
        </SelectPortal>
      </div>
    </SelectRoot>

    <input
      v-if="name"
      ref="hiddenInput"
      type="hidden"
      :name="name"
      :value="selectedIds"
      :disabled="disabled"
    />

    <!-- The caption under the field: field/HelperText.vue, the one copy every Zone field shares. -->
    <HelperText
      v-if="hasHelper"
      :id="helperId"
      :invalid="invalid"
      :warning="warning"
    >
      <slot name="helper">{{ helperText }}</slot>
    </HelperText>

    <slot name="notice" />
  </div>
</template>

<script lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  SelectRoot,
  SelectTrigger,
  SelectPortal,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
} from 'reka-ui';
import ZoneIcon, { type IconName } from './Icon.vue';
import HelperText from './field/HelperText.vue';
import ZoneAvatar from './Avatar.vue';
import ZoneSearchInput from './SearchInput.vue';

export interface SelectOption {
  id: string | number;
  name: string;
  description?: string;
  avatar?: string;
  dot?: string;
  /** A Zone icon name leading the row (Figma's floating-menu item with an icon, e.g. `home` on the current year). */
  icon?: IconName;
  disabled?: boolean;
}

export interface SelectResource {
  singular: string;
  plural: string;
}

/** Everything the `fetcher` needs, handed over on every change — see the guide above. */
export interface SelectQueryState {
  page: number;
  pageSize: number;
  search: string;
  /**
   * Always FALSE for the list queries the dropdown makes — the "Show only selected" toggle that
   * used to drive it is gone. Kept in the contract because `hydrateSelectedLabels` still sets it
   * true to ask the same fetcher to name preselected ids, so existing fetchers need no change.
   */
  selectedOnly: boolean;
  selected: (string | number)[];
}

export interface SelectFetchResult {
  options: SelectOption[];
  /** Omit and it is inferred as `options.length >= pageSize`. */
  hasMore?: boolean;
  totalCount?: number;
}

export type SelectSize = 'sm' | 'md' | 'lg';
export type SelectType = 'contained' | 'inline';
export type SelectModelValue = string | number | (string | number)[] | null;

type FetchResultLike = SelectFetchResult | SelectOption[];


// Figma: 32 / 40 / 48px tall; the value type steps up to 16px only at the large size.
const SIZE_CLASSES: Record<SelectSize, { trigger: string; text: string }> = {
  sm: { trigger: 'h-8', text: 'text-sm leading-5' },
  md: { trigger: 'h-10', text: 'text-sm leading-5' },
  lg: { trigger: 'h-12', text: 'text-base leading-6' },
};

function normalizeResult(result: FetchResultLike): SelectFetchResult {
  if (Array.isArray(result)) {
    return { options: result };
  }

  return {
    options: result?.options ?? [],
    hasMore: result?.hasMore,
    totalCount: result?.totalCount,
  };
}
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneSelect' });

const { t } = useI18n();

const props = defineProps({
  /** The selected id (or null), or an array of ids with `multiple`. */
  modelValue: {
    type: [String, Number, Array] as PropType<SelectModelValue>,
    default: null,
  },
  /**
   * Renders a hidden `<input>` holding dash-joined ids for Rails, dispatching a native `change` so
   * vee-validate's ValidationProvider sees it.
   */
  name: {
    type: String,
    default: null,
  },
  /** The rows in local mode; see the Options section. Ignored once `fetcher` is passed. */
  options: {
    type: Array as PropType<SelectOption[]>,
    default: () => [],
  },
  /** Label text (the `#label` slot replaces the label element). */
  label: {
    type: String,
    default: null,
  },
  /** Info-tooltip text beside the label; still rendered with `#label`. */
  tooltip: {
    type: String,
    default: null,
  },
  /** The caption under the field (or the `#helper` slot). */
  helperText: {
    type: String,
    default: null,
  },
  /** Multi-select: the v-model becomes an array and the trigger shows a count pill. */
  multiple: {
    type: Boolean,
    default: false,
  },
  /**
   * Opt in to the in-dropdown search field. Opening the dropdown then puts the caret straight in
   * it.
   */
  searchable: {
    type: Boolean,
    default: false,
  },
  /** Trigger text while nothing is selected. */
  placeholder: {
    type: String,
    default: null,
  },
  /** Shown when no row matches. */
  emptyText: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Figma's read-only look, reached by disabling the trigger (a `<button>` has no readonly). */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** The × that clears the selection: in the count pill of a `multiple` select, before the chevron of a single one. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Red styling and `aria-invalid` on the trigger. */
  invalid: {
    type: Boolean,
    default: false,
  },
  /** Figma's "Alert" warning state: yellow caption + warning icon. `invalid` wins over it. */
  warning: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  /** 32 / 40 / 48px tall. */
  size: {
    type: String as PropType<SelectSize>,
    default: 'md',
    validator: (v: string) => ['sm', 'md', 'lg'].includes(v),
  },
  /** `inline` drops the contained field chrome. */
  type: {
    type: String as PropType<SelectType>,
    default: 'contained',
    validator: (v: string) => ['contained', 'inline'].includes(v),
  },
  /** `{ singular, plural }`: labels the count summary, "All …" and the search text. */
  resource: {
    type: Object as PropType<SelectResource>,
    default: () => ({ singular: 'Item', plural: 'Items' }),
  },
  /** Server mode: one function for search and pagination. See the Modes section. */
  fetcher: {
    type: Function as PropType<(state: SelectQueryState) => FetchResultLike | Promise<FetchResultLike>>,
    default: null,
  },
  /** Rows per page — the local render window as well as the `pageSize` sent to the fetcher. */
  pageSize: {
    type: Number,
    default: 25,
  },
  /** Milliseconds to wait after the last keystroke before re-querying the fetcher. */
  searchDebounce: {
    type: Number,
    default: 300,
  },
});

const emit = defineEmits<{
  /** The new selection: an id or null, an array with `multiple`. */
  'update:modelValue': [value: SelectModelValue];
  /** Fires with `update:modelValue`, for form wiring. */
  change: [value: SelectModelValue];
  /** Every keystroke in the search box. */
  'update:search': [query: string];
  /** The `fetcher` rejected. */
  'fetch-error': [error: unknown];
}>();

const slots = defineSlots<{
  /**
   * Replaces the built-in label element; it does not render inside it, so render your own ZoneLabel in it
   * to keep the standard look. `label` is the prop's text, so markup beside the label need not restate it.
   * The `label` prop and the required `*` are not rendered meanwhile; the `tooltip` still is, beside your
   * content.
   */
  label?(props: { label: string | null; labelId: string; required: boolean; invalid: boolean }): any;
  /** The caption under the field; overrides `helperText`. */
  helper?(): any;
  /** Free-form content below the field. Unstyled. */
  notice?(): any;
}>();

// Template refs.
const searchInput = ref<{ $el?: HTMLElement } | null>(null);
const sentinel = ref<HTMLElement | null>(null);
const hiddenInput = ref<HTMLInputElement | null>(null);

const isOpen = ref(false);
const searchQuery = ref('');
// The selection as it stood when the dropdown was opened. Drives the "selected first"
// grouping and is deliberately NOT the live selection — see `orderedOptions`.
const selectedAtOpen = ref(new Set<string | number>());
// The <label>'s id, referenced by the trigger's aria-labelledby (the trigger is a Reka button, so
// we associate via aria-labelledby rather than a native label `for`/input `id`), and the caption's.
const id = `zone-select-${useId()}`;
const labelId = `${id}-label`;
const helperId = `${id}-helper`;
// Paging cursor — the render window in local mode, the requested page in server mode.
const page = ref(1);
const fetchedOptions = ref<SelectOption[]>([]);
const hasMore = ref(false);
const isFetching = ref(false);
let fetchToken = 0;
// Labels for ids that are no longer (or never were) in the visible list, so the trigger can
// name a selection after the dropdown closes or before it has ever been opened.
const optionsCache = ref<Record<string, SelectOption>>({});
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let focusTimer: ReturnType<typeof setTimeout> | null = null;
let observer: IntersectionObserver | null = null;

const isServerMode = computed<boolean>(() => Boolean(props.fetcher));

const hasLabel = computed<boolean>(() => Boolean(props.label || slots.label));

const hasHelper = computed<boolean>(() => Boolean(props.helperText || slots.helper));

const labelText = computed<string>(() => (props.type === 'inline' ? `${props.label}:` : props.label));

const selected = computed<Set<string | number>>(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    return new Set();
  }
  if (Array.isArray(props.modelValue)) {
    return new Set(props.modelValue);
  }

  return new Set([props.modelValue]);
});

const selectedCount = computed<number>(() => selected.value.size);

const countLabel = computed<string>(() => (selectedCount.value > 99 ? '99+' : String(selectedCount.value)));

/** The list the dropdown draws from: the server's answer verbatim, or the caller's options. */
const sourceOptions = computed<SelectOption[]>(() => (isServerMode.value ? fetchedOptions.value : props.options));

function optionById(id: string | number): SelectOption | undefined {
  return sourceOptions.value.find((option) => option.id === id)
    ?? props.options.find((option) => option.id === id)
    ?? optionsCache.value[String(id)];
}

function isSelected(id: string | number): boolean {
  return selected.value.has(id);
}

function isItemDisabled(item: SelectOption): boolean {
  return !!item.disabled;
}

const singleSelectedItem = computed<SelectOption | undefined>(() => {
  if (props.multiple || selectedCount.value !== 1) { return undefined; }
  const [id] = selected.value;

  return optionById(id);
});

/** Ids the component cannot name yet — the trigger of an async select would show a bare count. */
const hasUnresolvedSelection = computed<boolean>(() => Array.from(selected.value).some((id) => !optionById(id)));

const hasLeading = computed<boolean>(() => {
  if (props.multiple) { return selectedCount.value > 0; }

  return Boolean(singleSelectedItem.value?.avatar || singleSelectedItem.value?.dot);
});

const showClear = computed<boolean>(() => props.clearable && selectedCount.value > 0 && !props.readonly && !props.disabled);

/**
 * Local mode filters here; server mode does NOT — the fetcher was given the search term and
 * `selectedOnly` and its answer is rendered as-is, so a server that matches on a field this
 * component cannot see (an email, an external code) still returns usable rows.
 */
const filteredOptions = computed<SelectOption[]>(() => {
  if (isServerMode.value) {
    return sourceOptions.value;
  }

  let opts = sourceOptions.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();

    opts = opts.filter((option) => (
      option.name.toLowerCase().includes(query)
      || (option.description ?? '').toLowerCase().includes(query)
    ));
  }

  return opts;
});

/**
 * `filteredOptions` re-ordered so the rows that were already selected come first, each group
 * keeping its original relative order.
 *
 * The partition is keyed on `selectedAtOpen` — a SNAPSHOT taken when the dropdown opened —
 * never on the live selection. Grouping on the live set would yank a row to the top of the
 * list the instant you ticked it, moving everything else under the pointer mid-interaction.
 * Keyed on the snapshot, the order holds still for as long as the dropdown is open and a
 * REOPEN regroups: exactly the "on reopen, selected options are grouped at the top" behaviour
 * asked for. Runs ahead of `visibleOptions`, so the selected rows are in the first page rather
 * than somewhere past the render window.
 *
 * In SERVER mode this can only group the rows already loaded — the same limitation that
 * "All {plural}" has, and for the same reason.
 */
const orderedOptions = computed<SelectOption[]>(() => {
  // Multi only: the grouping stands in for the old "Show only selected" toggle, which a single
  // select never had — its one value is already in the trigger, and hoisting it would scramble an
  // ordinal list (years, months) for nothing.
  if (!props.multiple || !selectedAtOpen.value.size) {
    return filteredOptions.value;
  }

  const wasSelected = (option: SelectOption): boolean => selectedAtOpen.value.has(option.id);
  const hoisted = filteredOptions.value.filter(wasSelected);

  // Nothing on offer was selected when we opened — skip building a second array.
  if (!hoisted.length) {
    return filteredOptions.value;
  }

  return [...hoisted, ...filteredOptions.value.filter((option) => !wasSelected(option))];
});

/**
 * The rows the master checkbox actually speaks for: everything currently on offer that can
 * be picked. Disabled rows are excluded so a list where the only unselected row is disabled
 * still reads as fully checked — `onSelectAll` skips them too, so including them here would
 * leave the box stuck on 'mixed' with no click able to resolve it.
 */
const selectableOptions = computed<SelectOption[]>(() => orderedOptions.value.filter((option) => !isItemDisabled(option)));

/**
 * ARIA's tri-state, and the single source of truth for the glyph and the click. 'mixed' is
 * the ARIA spelling of a partial selection.
 *
 * Scoped to the rows on offer, so with a search active "All members" means "all matching
 * members" — the same set `onSelectAll` adds and `onDeselectAll` removes, which is what keeps
 * the box's appearance and its click in agreement. In SERVER mode with more pages to come it
 * can only speak for what is loaded, as documented in the guide.
 */
const selectAllState = computed<'true' | 'false' | 'mixed'>(() => {
  const selectable = selectableOptions.value;

  if (!selectable.length) {
    return 'false';
  }

  const picked = selectable.filter((option) => isSelected(option.id)).length;

  if (picked === 0) {
    return 'false';
  }

  return picked === selectable.length ? 'true' : 'mixed';
});

// `check` matches the tick on the option rows below; `check_indeterminate_small` is the same
// dash ZoneCheckbox draws for its Figma "Intermediate" state, so the two agree.
const selectAllGlyph = computed<string | null>(() => {
  if (selectAllState.value === 'true') {
    return 'check';
  }

  return selectAllState.value === 'mixed' ? 'check_indeterminate_small' : null;
});

/** Only this slice reaches the DOM, so a huge local `options` array stays cheap to render. */
const visibleOptions = computed<SelectOption[]>(() => {
  if (isServerMode.value) {
    return orderedOptions.value;
  }

  return orderedOptions.value.slice(0, page.value * props.pageSize);
});

const canLoadMore = computed<boolean>(() => {
  if (isFetching.value) {
    return false;
  }
  if (isServerMode.value) {
    return hasMore.value;
  }

  return visibleOptions.value.length < orderedOptions.value.length;
});

const queryState = computed<SelectQueryState>(() => ({
  page: page.value,
  pageSize: props.pageSize,
  search: searchQuery.value,
  // Always false for a LIST query now that the "Show only selected" toggle is gone. The
  // flag stays in the contract because `hydrateSelectedLabels` still overrides it to true
  // to name preselected ids, so existing fetchers keep working unchanged.
  selectedOnly: false,
  selected: Array.from(selected.value),
}));

const triggerDisabled = computed<boolean>(() => props.disabled || props.readonly);

const triggerClasses = computed<string>(() => {
  const classes = [
    'relative flex w-full items-center rounded-md border border-solid p-1 text-left transition-colors focus:outline-none',
    SIZE_CLASSES[props.size].trigger,
  ];

  if (props.readonly) {
    // Figma read-only: gray-50 fill, no chevron, value in gray-900.
    classes.push('bg-gray-50 border-gray-300 cursor-default');
  } else {
    classes.push('bg-white');

    if (props.invalid) {
      classes.push('border-red-600');
    } else if (isOpen.value) {
      // Figma: 1px primary-700 while open, 2px on focus — a border weight change, not a ring.
      classes.push('border-primary-700');
    } else {
      classes.push('border-gray-300 hover:border-gray-400 focus:border-2 focus:border-primary-700');
    }

    classes.push(props.disabled ? 'cursor-not-allowed' : 'cursor-pointer');
  }

  return classes.join(' ');
});

const selectionClasses = computed<string>(() => {
  const classes = ['block min-w-0 flex-1 truncate', SIZE_CLASSES[props.size].text];

  // The placeholder's weight is stated, not inherited. This trigger is a <button>, which the
  // scoped preflight gives `font-weight: inherit`, so it picks up whatever the host page
  // happens to set — 400 in the Vue gallery, a coin toss on a legacy page. `font-regular`
  // pins it to the 400 the design asks for either way. (This is a <span>, not a real
  // ::placeholder, so it needs the bare utility rather than the `placeholder:` variant.)
  classes.push(selectedCount.value ? 'text-gray-900' : 'font-regular text-gray-400');

  return classes.join(' ');
});

const resourceName = computed<string>(() => (selectedCount.value === 1 ? props.resource.singular : props.resource.plural));

const placeholder = computed<string>(() => props.placeholder ?? t('placeholder'));
const emptyText = computed<string>(() => props.emptyText ?? t('empty'));
const selectionText = computed<string>(() => {
  if (!selectedCount.value) {
    return placeholder.value;
  }
  // Figma's Multi "Active" state is always "<count pill> <resource>" — never the item's label.
  if (props.multiple) {
    return resourceName.value;
  }

  return singleSelectedItem.value?.name ?? placeholder.value;
});

const wrapperClasses = computed<string>(() => {
  const classes = [];

  classes.push(props.type === 'inline'
    ? 'inline-flex items-center gap-1'
    : 'flex flex-col gap-1');

  // Figma disables the WHOLE field at 30%, label and caption included — not just the trigger.
  if (props.disabled) {
    classes.push('opacity-30');
  }

  return classes.join(' ');
});

// Kept identical to ZoneLabel's own `labelClasses` — Select renders its own label row (it
// needs the inline type's leading icon and trailing colon, which ZoneLabel cannot express),
// so the two have to be changed together. `m-0` neutralises the legacy bootstrap `label`
// rules; the colour sits on the <label> itself, never inherited. See Label.vue's NOTES.
const labelClasses = computed<string>(() => {
  const color = props.invalid ? 'text-red-600' : 'text-gray-400';

  return `m-0 ${color} text-xs font-medium uppercase leading-4`;
});

// Beside the <label>, not inside it, so the icon states the label's colour itself.
const tooltipIconClasses = computed<string>(() => (props.invalid ? 'text-red-600' : 'text-gray-600'));


const selectedIds = computed<string>(() => Array.from(selected.value).join('-'));

const searchPlaceholder = computed<string>(() => t('search_placeholder', { plural: props.resource.plural }));

function onModelValueUpdate(value: SelectModelValue) {
  emit('update:modelValue', value);
  emit('change', value);
}

function optionRowClasses(item: SelectOption): string {
  // Reka sets data-highlighted on the keyboard-focused row; give it the hover treatment so
  // pointer and keyboard read the same.
  const base = 'hover:bg-gray-50 data-[highlighted]:bg-gray-50';

  return isSelected(item.id) ? `bg-primary-50 ${base}` : base;
}

// Same affordance (and the same shades) as ZoneFloatingMenu's checkbox rows: Figma's Blue 700,
// the fill ZoneCheckbox uses, NOT ZoneButton's lighter primary-500. `border-transparent` keeps
// the box a flat 16px in both states, so it never shifts by a pixel as it fills. Keep this pair
// equal to ZoneCheckbox's `boxColorClasses` and ZoneFloatingMenu's `CHECKBOX_*` constants.
function checkboxClasses(checked: boolean): string {
  const base = 'flex size-4 shrink-0 items-center justify-center rounded border';

  return checked
    ? `${base} border-transparent bg-primary-700 text-white`
    : `${base} border-gray-300 bg-white`;
}

function itemKey(item: SelectOption, index: number): string | number {
  return item.id ?? index;
}

/**
 * Put the caret in the search box on open — and keep it there through Reka's own focus pass.
 *
 * Reka's SelectContentImpl prevents its FocusScope's mount-auto-focus, but it separately runs
 * `watch(isPositioned, focusSelectedItem)`, and `focusSelectedItem()` moves focus to the
 * selected row. `isPositioned` only flips once floating-ui has placed the popper — a frame or
 * so AFTER mount — so a single `nextTick` focus was silently overwritten. It also no-ops
 * when nothing is selected, which is why an empty select appeared to work and a filled one
 * did not.
 *
 * Hence: claim focus now, then re-claim it after the positioning pass has had its turn. Each
 * attempt bails when the dropdown has closed, when search is off, or when the input already
 * holds focus, so the extra passes are free and never fight the user.
 */
function focusSearch() {
  const attempt = () => {
    if (!isOpen.value || !props.searchable) {
      return;
    }

    const root = searchInput.value?.$el;
    const input = root?.querySelector('input');

    if (!input || document.activeElement === input) {
      return;
    }

    input.focus();
  };

  attempt();

  // Two frames: floating-ui positions on the first, so `isPositioned` (and Reka's focus)
  // lands by the second.
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      attempt();
      requestAnimationFrame(attempt);
    });
  }

  // Backstop for environments where rAF is throttled or absent (a background tab, jsdom).
  if (focusTimer) {
    clearTimeout(focusTimer);
  }
  focusTimer = setTimeout(attempt, 0);
}

function onSearchInput(value: string) {
  searchQuery.value = value ?? '';
  emit('update:search', searchQuery.value);
  // Local mode re-filters through `filteredOptions` — just rewind the render window.
  page.value = 1;

  if (!isServerMode.value) {
    return;
  }
  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
  if (props.searchDebounce <= 0) {
    runFetch();

    return;
  }
  searchTimer = setTimeout(() => {
    searchTimer = null;
    runFetch();
  }, props.searchDebounce);
}

// Reka's SelectContent owns keydown for typeahead and arrow navigation. Typing in the search
// box must not feed that typeahead, but Escape (close) and the arrows (move the highlight into
// the list) still have to reach it.
function onSearchKeydown(event: KeyboardEvent) {
  if (['Escape', 'ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(event.key)) {
    return;
  }

  event.stopPropagation();
}

function onClearAll() {
  const next: SelectModelValue = props.multiple ? [] : null;

  onModelValueUpdate(next);
}

// The in-pill × cannot be focusable (it lives inside Reka's trigger <button>), so this is the
// keyboard route to clearing a selection.
function onTriggerKeydown(event: KeyboardEvent) {
  if (!['Backspace', 'Delete'].includes(event.key)) {
    return;
  }
  if (triggerDisabled.value || !props.clearable || !selectedCount.value) {
    return;
  }

  event.preventDefault();
  onClearAll();
}

function onSelectAll() {
  // ADDS the offered rows to the selection rather than replacing it with them. The two differ
  // whenever the offered set is narrower than the whole: search for "Ada", hit "All members",
  // and a replace would silently drop every member you had already picked that does not match
  // "Ada". It also keeps a selected-but-DISABLED row (which `selectableOptions` excludes, so
  // no click of this row can reach it) and, in server mode, ids from pages that are no longer
  // loaded. Existing ids keep their order and new ones append, so the value the caller handed
  // us is never reshuffled — and with nothing selected this is still exactly the old result.
  //
  // `selectableOptions`, NOT `visibleOptions`: in local mode the latter is only the render
  // window — `slice(0, page * pageSize)` — so selecting from it would take the first 25 rows
  // and call that "all", growing the answer as the user happened to scroll. `selectableOptions`
  // is the whole local collection minus disabled rows, still narrowed by the search, which is
  // the set the row is offering — and the same set `selectAllState` reports on, so the box and
  // the click can never disagree.
  const offered = selectableOptions.value.map((option) => option.id);
  const added = offered.filter((id) => !selected.value.has(id));

  onModelValueUpdate([...selected.value, ...added]);
}

/**
 * The master checkbox's un-check: removes the rows it is offering and leaves everything else
 * selected. The mirror image of `onSelectAll`, and it has to be — a button whose check unions
 * but whose uncheck wiped the lot would lose a filtered-out selection on the second click of
 * the same control.
 *
 * Deliberately NOT `onClearAll`, which stays a true clear-everything for the count pill's ×
 * and for Backspace/Delete on the trigger. Those two are asking for the whole selection to
 * go; this one is only speaking for the rows on screen.
 */
function onDeselectAll() {
  const offered = new Set(selectableOptions.value.map((option) => option.id));

  onModelValueUpdate(Array.from(selected.value).filter((id) => !offered.has(id)));
}

/**
 * The master checkbox's click, driven off `selectAllState` so it is the exact inverse of what
 * the box is showing: fully checked → clear, anything else (empty OR mixed) → select all. The
 * mixed→all→none cycle the design asks for falls out of that, and it never leaves a click
 * that appears to do nothing.
 */
function onToggleAll() {
  if (selectAllState.value === 'true') {
    onDeselectAll();

    return;
  }

  onSelectAll();
}

// ── lazy loading ──────────────────────────────────────────────────────────────────────────────

function loadMore() {
  if (!canLoadMore.value) {
    return;
  }

  page.value += 1;

  if (isServerMode.value) {
    runFetch({ append: true });
  }
}

// Belt-and-braces companion to the IntersectionObserver, for browsers/environments where the
// sentinel never reports (a zero-height viewport, an observer that has not ticked yet).
function onViewportScroll(event: Event) {
  const el = event.target as HTMLElement | null;

  if (!el) {
    return;
  }
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
    loadMore();
  }
}

function teardownObserver() {
  observer?.disconnect();
  observer = null;
}

function syncObserver() {
  teardownObserver();

  if (!sentinel.value || typeof IntersectionObserver === 'undefined') {
    return;
  }

  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      loadMore();
    }
  }, { rootMargin: '200px' });
  observer.observe(sentinel.value);
}

// ── server fetching ───────────────────────────────────────────────────────────────────────────

function cacheOptions(options: SelectOption[]) {
  if (!options.length) {
    return;
  }

  const next = { ...optionsCache.value };

  options.forEach((option) => { next[String(option.id)] = option; });
  optionsCache.value = next;
}

async function runFetch({ append = false }: { append?: boolean } = {}) {
  if (!props.fetcher) {
    return;
  }

  fetchToken += 1;
  const token = fetchToken;

  isFetching.value = true;

  try {
    const result = normalizeResult(await props.fetcher(queryState.value));

    // A slower earlier request must never overwrite a newer answer.
    if (token !== fetchToken) {
      return;
    }

    fetchedOptions.value = append
      ? [...fetchedOptions.value, ...result.options]
      : result.options;
    hasMore.value = typeof result.hasMore === 'boolean'
      ? result.hasMore
      : result.options.length >= props.pageSize;
    cacheOptions(result.options);
  } catch (error) {
    if (token === fetchToken) {
      emit('fetch-error', error);
    }
  } finally {
    if (token === fetchToken) {
      isFetching.value = false;
    }
  }
}

/**
 * Asks the SAME fetcher for just the selected records, so a preselected id can be named in the
 * trigger before the dropdown has ever been opened. Deliberately does not touch the visible
 * list or `hasMore` — it only fills the label cache.
 */
async function hydrateSelectedLabels() {
  if (!props.fetcher || !hasUnresolvedSelection.value) {
    return;
  }

  try {
    const result = normalizeResult(await props.fetcher({
      ...queryState.value,
      page: 1,
      search: '',
      selectedOnly: true,
    }));

    cacheOptions(result.options);
  } catch (error) {
    emit('fetch-error', error);
  }
}

// ValidationProvider detects field changes by listening for native DOM events bubbling up
// through its wrapper div. ZoneSelect is a custom listbox with no native <select>, so Vue
// component events never reach that listener. Dispatching a native change event on the hidden
// input (whose value is selectedIds) lets ValidationProvider read the current value via
// event.target.value without requiring an explicit :model-value prop at every call site.
watch(() => props.modelValue, () => {
  nextTick(() => {
    hiddenInput.value?.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // A value set from outside (a URL param, a form reset) can name ids this component has
  // never fetched. Ask for their labels while the dropdown is closed, so the trigger is
  // correct without the user having to open it.
  if (!isOpen.value) {
    hydrateSelectedLabels();
  }
});

watch(isOpen, (open) => {
  if (open) {
    page.value = 1;
    // Freeze the selection this open session groups by. Taken BEFORE any fetch, so a server
    // list is grouped by what was selected on open rather than by whatever came back.
    selectedAtOpen.value = new Set(selected.value);
    if (isServerMode.value) {
      fetchedOptions.value = [];
      runFetch();
    }
    if (props.searchable) {
      nextTick(() => focusSearch());
    }
  } else {
    searchQuery.value = '';
    page.value = 1;
    teardownObserver();
    // `selectedAtOpen` is deliberately left alone: re-snapshotted on the next open anyway,
    // and clearing it here would visibly re-order the list during the close transition.
  }

  nextTick(() => syncObserver());
});

// Re-arm the sentinel whenever it is mounted/unmounted by a list change.
watch(canLoadMore, () => {
  nextTick(() => syncObserver());
});

onMounted(() => {
  hydrateSelectedLabels();
});

onBeforeUnmount(() => {
  teardownObserver();
  if (searchTimer) {
    clearTimeout(searchTimer);
  }
  if (focusTimer) {
    clearTimeout(focusTimer);
  }
});
</script>

<i18n lang="json">
{
  "en": {
    "placeholder": "Select",
    "empty": "No results",
    "all": "All {plural}",
    "loading": "Loading…",
    "search_placeholder": "Search {plural}..."
  }
}
</i18n>
